import React, { useMemo } from 'react'
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import OrderRowActions from '../../common/OrderRowActions'
import { statusBadgeSx } from '../../common/sharedStyles'
import { fmtBytes, fmtDateTime, fmtDay, fmtNum, MODE_META, policyMode, reclaimableBytes, resumeModeLabel, sendsToS3, statusLabel, statusTone } from './archivalUtils'

const NO_SUMMARY = 'No summary yet — click Refresh sizes'
const badgeSx = (tone) => ({ ...statusBadgeSx(tone), fontSize: '0.6875rem', py: 0, whiteSpace: 'nowrap' })
const lineSx = { fontSize: '0.8125rem', lineHeight: 1.4, whiteSpace: 'nowrap' }
const subSx = { fontSize: '0.75rem', lineHeight: 1.4, color: 'text.secondary', whiteSpace: 'nowrap' }

const range = (a, b) => (a || b ? `${a || '—'} → ${b || '—'}` : '—')

function TipLines({ lines }) {
    return (
        <Box component='span' sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
            {lines.map(([k, v]) => (
                <Box component='span' key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <span>{k}</span><span>{v}</span>
                </Box>
            ))}
        </Box>
    )
}

/** Docs on line 1, size on line 2; details (range, index / free sizes) in the tooltip. */
function TierCell({ hasSummary, docs, size, tip }) {
    if (!hasSummary) {
        return (
            <Tooltip title={NO_SUMMARY} arrow>
                <Typography sx={{ ...lineSx, color: 'text.disabled', width: '100%', textAlign: 'right' }}>—</Typography>
            </Tooltip>
        )
    }
    return (
        <Tooltip title={<TipLines lines={tip} />} arrow placement='top'>
            <Box sx={{ width: '100%', textAlign: 'right', cursor: 'default' }}>
                <Typography sx={{ ...lineSx, fontWeight: 600 }}>{fmtNum(docs)}</Typography>
                <Typography sx={subSx}>{fmtBytes(size)}</Typography>
            </Box>
        </Tooltip>
    )
}

/**
 * One row per archived collection: on/off, retention, size per tier (live collection, archive collection, S3),
 * last run, and the actions. Actions are raised to the page (onAction(kind, item)), which owns the dialogs.
 */
export default function CollectionsTable({ collections, globalS3Enabled, loading, onAction }) {
    const rows = useMemo(() => collections.map((c) => ({ id: c.collectionName, ...c })), [collections])

    const columns = useMemo(() => [
        {
            field: 'collectionName', headerName: 'Collection', minWidth: 200, flex: 1, sortable: false,
            renderCell: ({ row }) => {
                const p = row.policy
                return (
                    <Stack spacing={0.5} sx={{ minWidth: 0, py: 0.5 }}>
                        <Tooltip title={p?.description || ''} arrow placement='top-start' disableHoverListener={!p?.description}>
                            <Typography noWrap sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>{row.collectionName}</Typography>
                        </Tooltip>
                        <Stack direction='row' alignItems='center' sx={{ gap: 0.5 }}>
                            {p ? (
                                <Tooltip title={MODE_META[policyMode(p)].help} arrow placement='bottom-start'>
                                    <Box component='span' sx={badgeSx(MODE_META[policyMode(p)].tone)}>{MODE_META[policyMode(p)].label}</Box>
                                </Tooltip>
                            ) : <Box component='span' sx={badgeSx('neutral')}>No policy</Box>}
                        </Stack>
                        {p && !p.enabled && p.disabledReason && (
                            <Tooltip title={p.disabledReason} arrow placement='bottom-start'>
                                <Typography noWrap sx={subSx}>{p.disabledReason}</Typography>
                            </Tooltip>
                        )}
                    </Stack>
                )
            },
        },
        {
            field: 'retention', headerName: 'Retention', width: 135, sortable: false,
            renderCell: ({ row }) => {
                const p = row.policy
                if (!p) return <Typography sx={{ ...lineSx, color: 'text.disabled' }}>—</Typography>
                return (
                    <Box>
                        <Typography sx={{ ...lineSx, fontWeight: 600 }}>{`${p.hotMonths} mo → ${p.warmMonths} mo`}</Typography>
                        <Typography sx={subSx}>{sendsToS3(p, globalS3Enabled) ? 'then S3' : 'then kept'}</Typography>
                    </Box>
                )
            },
        },
        {
            field: 'live', headerName: 'Live', width: 105, sortable: false, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                const m = row.summary?.main || {}
                return (
                    <TierCell hasSummary={Boolean(row.summary)} docs={m.docs} size={m.storageBytes} tip={[
                        ['Oldest', fmtDay(m.oldest)], ['Newest', fmtDay(m.newest)],
                        ['Indexes', fmtBytes(m.indexBytes)], ['Free (reusable)', fmtBytes(m.freeBytes)],
                    ]} />
                )
            },
        },
        {
            field: 'archive', headerName: 'Archive', width: 105, sortable: false, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                const w = row.summary?.warm || {}
                return (
                    <TierCell hasSummary={Boolean(row.summary)} docs={w.docs} size={w.storageBytes} tip={[
                        ['Collection', row.warmCollectionName || `${row.collectionName}_archive`],
                        ['Oldest', fmtDay(w.oldest)], ['Newest', fmtDay(w.newest)], ['Indexes', fmtBytes(w.indexBytes)],
                    ]} />
                )
            },
        },
        {
            field: 's3', headerName: 'S3', width: 100, sortable: false, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                const s = row.summary?.s3 || {}
                return (
                    <TierCell hasSummary={Boolean(row.summary)} docs={s.docs} size={s.bytes} tip={[
                        ['Months', range(s.oldestPeriod, s.newestPeriod)], ['Parts', fmtNum(s.parts)],
                    ]} />
                )
            },
        },
        {
            field: 'reclaimable', headerName: 'Reclaimable', width: 105, sortable: false, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                const bytes = reclaimableBytes(row.summary)
                if (bytes == null) {
                    return <Typography sx={{ ...lineSx, color: 'text.disabled', width: '100%', textAlign: 'right' }}>—</Typography>
                }
                return (
                    <Tooltip arrow placement='top' title={(
                        <Box>
                            <TipLines lines={[
                                ['Live', fmtBytes(row.summary?.main?.freeBytes || 0)],
                                ['Archive', fmtBytes(row.summary?.warm?.freeBytes || 0)],
                            ]} />
                            <Typography sx={{ fontSize: '0.75rem', mt: 0.5, maxWidth: 240 }}>
                                Freed by deletes but still held on disk. A compact returns it.
                            </Typography>
                        </Box>
                    )}>
                        <Typography sx={{ ...lineSx, fontWeight: 600, width: '100%', textAlign: 'right', cursor: 'default' }}>{fmtBytes(bytes)}</Typography>
                    </Tooltip>
                )
            },
        },
        {
            field: 'lastRun', headerName: 'Last run', width: 160, sortable: false,
            renderCell: ({ row }) => {
                const s = row.summary
                const active = [row.queued ? `${fmtNum(row.queued)} queued` : '', row.running ? `${fmtNum(row.running)} running` : ''].filter(Boolean).join(' / ')
                if (!s?.lastRunAt && !active) return <Typography sx={{ ...lineSx, color: 'text.disabled' }}>—</Typography>
                return (
                    // Date on top, status (and queued/running) underneath: one line was too narrow and clipped the chip.
                    <Stack spacing={0.5} sx={{ minWidth: 0, py: 0.5 }}>
                        <Typography noWrap sx={lineSx}>{s?.lastRunAt ? fmtDateTime(s.lastRunAt) : '—'}</Typography>
                        <Stack direction='row' alignItems='center' sx={{ gap: 0.75, minWidth: 0 }}>
                            {s?.lastStatus && <Box component='span' sx={{ ...badgeSx(statusTone(s.lastStatus)), flexShrink: 0 }}>{statusLabel(s.lastStatus)}</Box>}
                            {active && <Typography noWrap sx={{ ...subSx, color: 'info.dark' }}>{active}</Typography>}
                        </Stack>
                    </Stack>
                )
            },
        },
        {
            field: 'actions', headerName: '', width: 130, sortable: false, align: 'right',
            renderCell: ({ row }) => {
                const p = row.policy
                return (
                    <Stack direction='row' alignItems='center' justifyContent='flex-end' sx={{ gap: 0.75, width: '100%' }}>
                        <Tooltip title={p && !p.enabled ? 'Paused. Resume it from the ⋮ menu to run.' : ''}>
                            <span>
                                <Button size='small' variant='outlined' disabled={!p || !p.enabled} onClick={() => onAction('run', row)}
                                    sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', py: 0.25 }}>
                                    Run now
                                </Button>
                            </span>
                        </Tooltip>
                        {p && (
                            <OrderRowActions tooltip='More actions' actions={[
                                {
                                    key: 'toggle', label: p.enabled ? 'Pause' : `Resume (${resumeModeLabel(p)})`, color: p.enabled ? 'warning' : 'success',
                                    icon: p.enabled ? <PauseCircleOutlineIcon fontSize='small' /> : <PlayCircleOutlineIcon fontSize='small' />,
                                    onClick: () => onAction('toggle', row),
                                },
                                { key: 'edit', label: 'Edit retention', icon: <EditOutlinedIcon fontSize='small' />, onClick: () => onAction('edit', row) },
                                { key: 'rehydrate', label: 'Rehydrate month', icon: <RestoreOutlinedIcon fontSize='small' />, onClick: () => onAction('rehydrate', row) },
                            ]} />
                        )}
                    </Stack>
                )
            },
        },
    ], [globalS3Enabled, onAction])

    return (
        <ProzoDataGrid
            rows={rows}
            columns={columns}
            rowCount={rows.length}
            paginationMode='client'
            page={0}
            sizePerPage={100}
            hideFooter
            loading={loading}
            getRowId={(r) => r.id}
            // Fixed height: every row lines up, and autoHeight measures it exactly (auto row heights get under-measured).
            getRowHeight={() => 76}
            sx={{ '& .MuiDataGrid-cell': { fontVariantNumeric: 'tabular-nums', py: 1 } }}
        />
    )
}
